import { useState, useCallback, useRef, useEffect } from "react"
import cable from "@/shared/lib/cable"
import type { AiMessage, AiSource, AiConversation } from "@/entities/ai/types"
import { createConversation, sendMessage, getMessages, AiNotConfiguredError } from "./api"

interface StreamState {
  isStreaming: boolean
  streamedContent: string
}

export function useAiChat(workspaceSlug: string) {
  const [conversation, setConversation] = useState<AiConversation | null>(null)
  const [messages, setMessages] = useState<AiMessage[]>([])
  const [stream, setStream] = useState<StreamState>({ isStreaming: false, streamedContent: "" })
  const [error, setError] = useState<string | null>(null)
  const subscriptionRef = useRef<ReturnType<typeof cable.subscriptions.create> | null>(null)

  const subscribe = useCallback((conv: AiConversation) => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe()
    }

    subscriptionRef.current = cable.subscriptions.create(
      { channel: "AiChatChannel", conversation_id: conv.id },
      {
        received(data: { type: string; content?: string; message_id?: number; sources?: AiSource[]; message?: string }) {
          switch (data.type) {
            case "start":
              setStream({ isStreaming: true, streamedContent: "" })
              setError(null)
              break
            case "chunk":
              setStream(prev => ({
                isStreaming: true,
                streamedContent: prev.streamedContent + (data.content || ""),
              }))
              break
            case "done":
              setStream({ isStreaming: false, streamedContent: "" })
              // Refetch to get the full message from DB
              if (conv.id) {
                getMessages(workspaceSlug, conv.id).then(setMessages)
              }
              break
            case "error":
              setStream({ isStreaming: false, streamedContent: "" })
              setError(data.message || "Something went wrong")
              break
          }
        },
      }
    )
  }, [workspaceSlug])

  const startNewConversation = useCallback(async () => {
    const conv = await createConversation(workspaceSlug)
    setConversation(conv)
    setMessages([])
    setError(null)
    subscribe(conv)
    return conv
  }, [workspaceSlug, subscribe])

  const [needsSetup, setNeedsSetup] = useState(false)

  const send = useCallback(async (content: string) => {
    try {
      let conv = conversation
      if (!conv) {
        conv = await startNewConversation()
      }

      setError(null)
      const userMessage = await sendMessage(workspaceSlug, conv!.id, content)
      setMessages(prev => [...prev, userMessage])
    } catch (err) {
      if (err instanceof AiNotConfiguredError) {
        setNeedsSetup(true)
      } else {
        setError(err instanceof Error ? err.message : "Something went wrong")
      }
    }
  }, [conversation, workspaceSlug, startNewConversation])

  const loadConversation = useCallback(async (conv: AiConversation) => {
    setConversation(conv)
    const msgs = await getMessages(workspaceSlug, conv.id)
    setMessages(msgs)
    subscribe(conv)
  }, [workspaceSlug, subscribe])

  useEffect(() => {
    return () => {
      subscriptionRef.current?.unsubscribe()
    }
  }, [])

  return {
    conversation,
    messages,
    stream,
    error,
    needsSetup,
    send,
    startNewConversation,
    loadConversation,
  }
}
